// FILE: src/blueprint/components/navigation/StepNav.jsx
// GOAL:
// - Compact step navigation: First/Prev/Next/Last + clickable step numbers + jump select.
// - Uses ellipses for large step counts.
// - Info steps show "i". Question steps show ✓ / ✕ based on checkValidStep(step).

import { useMemo } from 'react';

function isInfoStep(step) {
  if (!step) return false;

  const kind = String(step?.kind || '').trim();
  if (kind === 'info') return true;

  const legacyType = String(step?.type || '').trim();

  return (
    legacyType === 'info' ||
    legacyType === 'video' ||
    legacyType === 'content' ||
    legacyType === 'system'
  );
}

function buildCompactNav(totalSteps, currentIdx, windowSize = 3) {
  if (!Number.isFinite(totalSteps) || totalSteps <= 0) return [];

  if (totalSteps <= 7) {
    return Array.from({ length: totalSteps }, (_, idx) => ({ type: 'num', idx }));
  }

  const items = [];
  const last = totalSteps - 1;

  items.push({ type: 'num', idx: 0 });

  const half = Math.floor(windowSize / 2);
  let start = currentIdx - half;
  let end = currentIdx + half;

  if (start < 1) {
    start = 1;
    end = start + (windowSize - 1);
  }

  if (end > last - 1) {
    end = last - 1;
    start = end - (windowSize - 1);
    if (start < 1) start = 1;
  }

  if (start > 1) items.push({ type: 'dots', key: 'L' });

  for (let i = start; i <= end; i += 1) {
    items.push({ type: 'num', idx: i });
  }

  if (end < last - 1) items.push({ type: 'dots', key: 'R' });

  items.push({ type: 'num', idx: last });

  return items;
}

function joinClasses(classes) {
  return classes.filter(Boolean).join(' ');
}

export default function StepNav({
  moduleTitle = '',
  steps = [],
  currentIndex = 0,
  onGoFirst,
  onGoLast,
  onGoPrev,
  onGoNext,
  onGoTo,
  isStepValid,
  answersVersion = 0,
}) {
  const safeSteps = Array.isArray(steps) ? steps : [];
  const total = safeSteps.length;

  if (total === 0) return null;

  const safeCurrentIndex = Math.min(Math.max(Number(currentIndex) || 0, 0), total - 1);

  const canGoPrev = safeCurrentIndex > 0;
  const canGoNext = safeCurrentIndex < total - 1;

  const compactItems = useMemo(() => {
    return buildCompactNav(total, safeCurrentIndex, 3);
  }, [total, safeCurrentIndex, answersVersion]);

  const jumpValue = String(safeCurrentIndex);

  const checkValidStep = (step) => {
    if (isInfoStep(step)) {
      return true;
    }

    if (typeof isStepValid !== 'function') {
      return true;
    }

    return Boolean(isStepValid(step));
  };

  const currentStep = safeSteps[safeCurrentIndex];
  const currentStepValid = checkValidStep(currentStep);

  return (
    <div className="dv-br-stepNav" role="navigation" aria-label="Step navigation">
      <div className="dv-br-stepNav__row dv-br-stepNav__row--controls">
        <div className="dv-br-stepNav__group">
          <button
            type="button"
            className="dv-br-stepNav__btn"
            onClick={onGoFirst}
            disabled={!canGoPrev}
            aria-label="First step"
            title="First"
          >
            &laquo;
          </button>

          <button
            type="button"
            className="dv-br-stepNav__btn"
            onClick={onGoPrev}
            disabled={!canGoPrev}
            aria-label="Previous step"
            title="Previous"
          >
            Prev
          </button>
        </div>

        <div className="dv-br-stepNav__group">
          <button
            type="button"
            className={joinClasses([
              'dv-br-stepNav__btn',
              currentStepValid ? 'is-valid' : 'is-invalid',
            ])}
            onClick={onGoNext}
            disabled={!canGoNext}
            aria-label="Next step"
            title="Next"
          >
            Next
          </button>

          <button
            type="button"
            className="dv-br-stepNav__btn"
            onClick={onGoLast}
            disabled={!canGoNext}
            aria-label="Last step"
            title="Last"
          >
            &raquo;
          </button>
        </div>
      </div>

      <div className="dv-br-stepNav__row dv-br-stepNav__row--numbers">
        <div className="dv-br-stepNav__numbers">
          {compactItems.map((item) => {
            if (item.type === 'dots') {
              return (
                <span
                  key={item.key}
                  className="dv-br-stepNav__dots"
                  aria-hidden="true"
                  title="More steps"
                >
                  …
                </span>
              );
            }

            const idx = item.idx;
            const step = safeSteps[idx];
            const active = idx === safeCurrentIndex;
            const info = isInfoStep(step);
            const valid = checkValidStep(step);
            const title = info ? 'Info' : valid ? 'Completed' : 'Incomplete';

            return (
              <button
                key={step?.id || idx}
                type="button"
                className={joinClasses([
                  'dv-br-stepNav__num',
                  active ? 'is-active' : '',
                  info ? 'is-info' : valid ? 'is-valid' : 'is-invalid',
                ])}
                onClick={() => onGoTo(idx)}
                aria-current={active ? 'step' : undefined}
                title={title}
              >
                <span className="dv-br-stepNav__numLabel">{idx + 1}</span>

                {info ? (
                  <span className="dv-br-stepNav__numIcon is-info" aria-hidden="true">
                    i
                  </span>
                ) : (
                  <span className="dv-br-stepNav__numIcon" aria-hidden="true">
                    {valid ? '✓' : '✕'}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="dv-br-stepNav__jump">
          <label className="dv-br-stepNav__jumpLabel" htmlFor="dv-br-stepNav-jump">
            Jump
          </label>

          <select
            id="dv-br-stepNav-jump"
            className="dv-br-stepNav__select"
            value={jumpValue}
            onChange={(e) => onGoTo(Number(e.target.value))}
          >
            {safeSteps.map((step, idx) => {
              const title = step?.title ? String(step.title) : '';
              const label = title ? `${idx + 1} — ${title}` : `${idx + 1}`;

              return (
                <option key={step?.id || idx} value={String(idx)}>
                  {label}
                </option>
              );
            })}
          </select>
        </div>
      </div>
    </div>
  );
}
